import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Clock, Trash2, Star, Search, X } from 'lucide-react';

const API_URL = '/api';

// Bucket a submission by age for the recency filter (exclusive buckets)
function getRecencyBucket(submittedAt) {
  const days = (Date.now() - new Date(submittedAt).getTime()) / 86400000;
  if (days <= 7) return '7';
  if (days <= 30) return '30';
  if (days <= 60) return '60';
  if (days <= 90) return '90';
  return 'older';
}

// Admin-authored intro text. Line breaks are kept and **double asterisks** mark bold;
// everything else renders as literal text, so no HTML from the admin reaches the page.
function IntroText({ text }) {
  return (
    <p className="text-sm text-gray-600 whitespace-pre-line">
      {String(text ?? '').split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith('**') && part.endsWith('**') && part.length > 4
          ? <strong key={i} className="font-semibold text-invizio-ink">{part.slice(2, -2)}</strong>
          : part
      )}
    </p>
  );
}

const QUESTION_TYPE_LABELS = {
  'multiple-choice': 'Multiple Choice',
  'multiple-select': 'Multiple Select',
  'short-answer': 'Short Answer',
  'fill-in': 'Fill In',
  'ordering': 'Ordering',
  'matching': 'Matching'
};

// Validate a question form (new or edited) and convert it to the API shape. Returns
// { error } or { payload }. The API stores answer keys as strings: option index(es) for
// choice types, one accepted answer per line for fill-in, one match per line (aligned with
// options) for matching; ordering has none because options are kept in the correct order.
function buildQuestionPayload(q) {
  if (!(q.text || '').trim()) return { error: 'Please enter question text.' };
  const options = (q.options || []).map(o => (o ?? '').trim());
  const { matches, ...rest } = q;
  if (q.type === 'multiple-choice' || q.type === 'multiple-select') {
    if (options.length === 0) return { error: 'Please add at least one answer option.' };
    const hasCorrect = q.type === 'multiple-choice'
      ? (typeof q.correct_answer === 'number' && q.correct_answer >= 0)
      : (Array.isArray(q.correct_answer) && q.correct_answer.length > 0);
    if (!hasCorrect) return { error: 'Please select at least one correct answer.' };
    return { payload: { ...rest, options, correct_answer: q.type === 'multiple-choice' ? String(q.correct_answer) : q.correct_answer.join(',') } };
  }
  if (q.type === 'fill-in') {
    if (!(q.correct_answer || '').trim()) return { error: 'Please enter at least one accepted answer.' };
    return { payload: { ...rest, options: null } };
  }
  if (q.type === 'ordering' || q.type === 'matching') {
    const noun = q.type === 'ordering' ? 'items' : 'pairs';
    if (options.length < 2) return { error: `Please add at least two ${noun}.` };
    if (options.some(o => !o) || new Set(options).size !== options.length) return { error: `Each ${q.type === 'ordering' ? 'item' : 'left-hand item'} must be filled in and different from the others.` };
    if (q.type === 'ordering') return { payload: { ...rest, options, correct_answer: null } };
    const matchList = (matches || []).map(m => (m ?? '').trim());
    if (matchList.length !== options.length || matchList.some(m => !m)) return { error: 'Every item needs a match.' };
    return { payload: { ...rest, options, correct_answer: matchList.join('\n') } };
  }
  return { payload: { ...rest, options: options.length > 0 ? options : null } };
}

// Normalize a question from API (DB): correct_answer is stored as index string(s). Parse to number (multiple-choice) or number[] (multiple-select) for form state.
function normalizeQuestionForEdit(q) {
  if (!q) return q;
  const out = { ...q };
  const opts = out.options;
  let options = Array.isArray(opts) ? opts : (typeof opts === 'string' ? (() => { try { return JSON.parse(opts); } catch (_) { return []; } })() : []);
  out.options = options;

  let ca = out.correct_answer;
  if (out.type === 'multiple-select') {
    if (typeof ca === 'string') {
      out.correct_answer = ca.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n >= 0);
    } else if (Array.isArray(ca) && ca.every(x => typeof x === 'number')) {
      out.correct_answer = ca;
    } else {
      out.correct_answer = [];
    }
  } else if (out.type === 'matching') {
    const matches = typeof ca === 'string' ? ca.split('\n') : [];
    out.matches = options.map((_, i) => matches[i] ?? '');
  } else if (out.type === 'multiple-choice' && options.length > 0) {
    if (typeof ca === 'string' && /^\d+$/.test(ca.trim())) {
      const idx = parseInt(ca, 10);
      out.correct_answer = idx >= 0 && idx < options.length ? idx : 0;
    } else if (typeof ca === 'number' && ca >= 0 && ca < options.length) {
      out.correct_answer = ca;
    } else {
      out.correct_answer = 0;
    }
  }
  return out;
}

// First URL path segment, lowercased: the assessment slug ('' for the root path)
function getPathSlug() {
  return (window.location.pathname.split('/').filter(Boolean)[0] || '').toLowerCase();
}

// CSRF token for the current admin session, set after login/check-auth. Sent back on every
// state-changing admin request as defense in depth alongside the SameSite=Lax session cookie.
let adminCsrfToken = null;

// Helper for fetch with credentials (for session cookies)
const fetchWithCredentials = (url, options = {}) => {
  // Build full URL if relative
  const fullUrl = url.startsWith('http') ? url : `${API_URL}${url.startsWith('/api') ? url.replace('/api', '') : url.startsWith('/') ? url : '/' + url}`;
  const method = (options.method || 'GET').toUpperCase();
  const needsCsrfToken = method !== 'GET' && method !== 'HEAD';

  return fetch(fullUrl, {
    ...options,
    credentials: 'include', // Include cookies for session management - CRITICAL for auth
    headers: {
      'Content-Type': 'application/json',
      ...(needsCsrfToken && adminCsrfToken ? { 'X-CSRF-Token': adminCsrfToken } : {}),
      ...options.headers
    }
  });
};

const TechnicalAssessment = () => {
  const [view, setView] = useState('candidate');
  const [candidateInfo, setCandidateInfo] = useState({
    date: new Date().toLocaleDateString('en-US'),
    fullName: '',
    email: ''
  });
  // Anti-spam: honeypot field bots tend to auto-fill, and a timestamp of when the
  // entry form first loaded so we can reject submissions completed implausibly fast.
  const [honeypot, setHoneypot] = useState('');
  const formLoadedAtRef = useRef(Date.now());

  // Set date automatically on mount
  useEffect(() => {
    setCandidateInfo(prev => ({
      ...prev,
      date: new Date().toLocaleDateString('en-US')
    }));
  }, []);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'new' | 'reviewed'
  const [recencyFilter, setRecencyFilter] = useState('all'); // 'all' | '7' | '30' | '60' | '90' | 'older'
  const [searchQuery, setSearchQuery] = useState('');
  const submissionListScrollY = useRef(0);
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [selectionAnchor, setSelectionAnchor] = useState(null); // id of the last plain-clicked row, for shift+click ranges
  const [adminPassword, setAdminPassword] = useState('');
  const [isAdminAuth, setIsAdminAuth] = useState(false);
  const [isGrading, setIsGrading] = useState(false);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [adminView, setAdminView] = useState('submissions');
  const [enabledQuestions, setEnabledQuestions] = useState([]);
  const [settings, setSettings] = useState({});
  const [settingsError, setSettingsError] = useState('');
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [exportEmail, setExportEmail] = useState('');
  const [exportStatus, setExportStatus] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionFormError, setQuestionFormError] = useState('');
  const questionFormSectionRef = useRef(null);
  const [newQuestion, setNewQuestion] = useState({
    text: '',
    type: 'multiple-choice',
    options: [],
    correct_answer: 0,
    points: 1,
    enabled: true
  });
  const [newOption, setNewOption] = useState('');
  const [anthropicKeyMissing, setAnthropicKeyMissing] = useState(false);
  const [isRegrading, setIsRegrading] = useState(false);
  // Assessment the candidate reached by URL: { slug, title, intro } (public fields only)
  const [currentAssessment, setCurrentAssessment] = useState(null);
  // Admin: all assessments, the one the Questions tab is showing, and the submissions filter
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(null);
  const [submissionAssessmentFilter, setSubmissionAssessmentFilter] = useState('all');
  const [assessmentForm, setAssessmentForm] = useState(null); // { id?, slug, title, intro, originalSlug? }
  const [assessmentFormError, setAssessmentFormError] = useState('');
  const [questionNotice, setQuestionNotice] = useState('');

  useEffect(() => {
    // Check authentication status on mount and verify before allowing admin access
    const checkAuthAndRoute = async () => {
      const isAdminRoute = window.location.pathname === '/admin' || window.location.pathname === '/admin/';
      
      try {
        const response = await fetchWithCredentials(`${API_URL}/admin/check-auth`);
        if (response.ok) {
          const data = await response.json();
          if (data.isAuthenticated) {
            adminCsrfToken = data.csrfToken || null;
            setIsAdminAuth(true);
            // Only set admin view if authenticated
            if (isAdminRoute) {
              setView('admin');
            }
          } else {
            // Not authenticated - only allow admin view if on admin route (for login)
            if (isAdminRoute) {
              setView('admin');
            }
          }
        } else {
          // Auth check failed - only allow admin view if on admin route
          if (isAdminRoute) {
            setView('admin');
          }
        }
      } catch (error) {
        console.log('Auth check failed (not logged in):', error.message);
        // Only allow admin view if on admin route (for login)
        if (isAdminRoute) {
          setView('admin');
        }
      }
    };

    checkAuthAndRoute();
  }, []);

  // Resolve the candidate's assessment from the URL whenever the landing page needs one
  // (on first load, and when leaving the admin panel, where the default is used)
  useEffect(() => {
    if (view !== 'candidate' || currentAssessment) return;
    const slug = getPathSlug();
    const loadCandidateAssessment = async () => {
      try {
        const response = await fetch(`${API_URL}/assessments/${slug && slug !== 'admin' ? encodeURIComponent(slug) : 'default'}`);
        if (response.status === 404) {
          setView('unavailable');
          return;
        }
        if (!response.ok) throw new Error('Failed to load assessment');
        setCurrentAssessment(await response.json());
      } catch (err) {
        console.error('Error loading assessment:', err);
        setError('Unable to load the assessment. Check your connection and refresh the page.');
      }
    };
    loadCandidateAssessment();
  }, [view, currentAssessment]);

  useEffect(() => {
    // Only load data if authenticated - this is critical for security
    if (isAdminAuth) {
      loadSubmissions();
      loadQuestions();
      loadSettings();
      loadAssessments();
      
      // Periodically verify authentication is still valid
      const authCheckInterval = setInterval(async () => {
        try {
          const response = await fetchWithCredentials(`${API_URL}/admin/check-auth`);
          if (response.ok) {
            const data = await response.json();
            if (!data.isAuthenticated) {
              // Session expired
              adminCsrfToken = null;
              setIsAdminAuth(false);
              setView('candidate');
              setSubmissions([]);
              setQuestions([]);
              setSettings({});
              setError('Your session has expired. Please log in again.');
              clearInterval(authCheckInterval);
            }
          } else {
            // Auth check failed
            adminCsrfToken = null;
            setIsAdminAuth(false);
            setView('candidate');
            setSubmissions([]);
            setQuestions([]);
            setSettings({});
            clearInterval(authCheckInterval);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          adminCsrfToken = null;
          setIsAdminAuth(false);
          setView('candidate');
          setSubmissions([]);
          setQuestions([]);
          setSettings({});
          clearInterval(authCheckInterval);
        }
      }, 60000); // Check every minute
      
      return () => clearInterval(authCheckInterval);
    } else {
      // Clear data when not authenticated
      setSubmissions([]);
      setQuestions([]);
      setSettings({});
      setAssessments([]);
    }
  }, [isAdminAuth]);

  useEffect(() => {
    if (showQuestionForm && questionFormSectionRef.current) {
      questionFormSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showQuestionForm, editingQuestion]);

  // Browser Back button support for submission detail view
  useEffect(() => {
    const handlePopState = (e) => {
      if (!e.state || !e.state.submissionView) {
        setSelectedSubmission(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Scroll to top when opening a submission; restore list scroll position when closing it
  useEffect(() => {
    if (selectedSubmission) {
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, submissionListScrollY.current);
    }
  }, [selectedSubmission]);

  // Clear row selection when the submissions list filters change
  useEffect(() => {
    setSelectedRowIds(new Set());
    setSelectionAnchor(null);
  }, [statusFilter, recencyFilter, searchQuery, submissionAssessmentFilter]);

  const loadSubmissions = async () => {
    // SECURITY: Only attempt to load if authenticated
    if (!isAdminAuth) {
      console.warn('Attempted to load submissions without authentication');
      return;
    }
    
    try {
      const response = await fetchWithCredentials(`${API_URL}/submissions`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        if (response.status === 401) {
          // Authentication failed - clear auth state
          setIsAdminAuth(false);
          setView('candidate');
          throw new Error('Authentication expired. Please log in again.');
        }
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to load submissions`);
      }
      const data = await response.json();
      setSubmissions(data);
      setError(null);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setError(`Failed to load submissions: ${error.message}`);
    }
  };

  const loadQuestions = async () => {
    // Only attempt to load if authenticated
    if (!isAdminAuth) {
      console.warn('Attempted to load questions without authentication');
      return;
    }
    
    try {
      // Use admin endpoint to get questions with correct answers for admin panel
      const response = await fetchWithCredentials(`${API_URL}/admin/questions`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        if (response.status === 401) {
          // Authentication failed - clear auth state
          setIsAdminAuth(false);
          setView('candidate');
          throw new Error('Authentication expired. Please log in again.');
        }
        throw new Error(errorData.error || errorData.details || `HTTP ${response.status}: Failed to load questions`);
      }
      const data = await response.json();
      setQuestions(data);
      setError(null);
    } catch (error) {
      console.error('Error loading questions:', error);
      setError(`Failed to load questions: ${error.message}`);
    }
  };

  const loadAssessments = async () => {
    try {
      const response = await fetchWithCredentials(`${API_URL}/admin/assessments`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      const data = await response.json();
      setAssessments(data);
      // Keep the Questions tab pointed at an assessment that still exists
      setSelectedAssessmentId(prev =>
        data.some(a => a.id === prev) ? prev : (data.find(a => a.isDefault) || data[0])?.id ?? null
      );
    } catch (error) {
      console.error('Error loading assessments:', error);
      setError(`Failed to load assessments: ${error.message}`);
    }
  };

  // Send an assessment change and refresh the list; returns the server's error message, if any
  const sendAssessmentRequest = async (url, method, body) => {
    const response = await fetchWithCredentials(url, {
      method,
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return errorData.error || `Request failed (HTTP ${response.status})`;
    }
    await loadAssessments();
    return null;
  };

  const saveAssessment = async () => {
    const { id, slug, title, intro, originalSlug } = assessmentForm;
    const normalizedSlug = (slug || '').trim().toLowerCase();
    if (id && normalizedSlug !== originalSlug && !window.confirm(
      `Changing the URL from /${originalSlug} to /${normalizedSlug} will break any links already sent to candidates. Continue?`
    )) {
      return;
    }
    const err = id
      ? await sendAssessmentRequest(`${API_URL}/admin/assessments/${id}`, 'PATCH', { slug, title, intro })
      : await sendAssessmentRequest(`${API_URL}/admin/assessments`, 'POST', { slug, title, intro });
    if (err) {
      setAssessmentFormError(err);
    } else {
      setAssessmentForm(null);
      setAssessmentFormError('');
    }
  };

  const runAssessmentAction = async (url, method, body) => {
    const err = await sendAssessmentRequest(url, method, body);
    setError(err);
  };

  const deleteAssessment = async (assessment) => {
    if (!window.confirm(`Delete the "${assessment.title}" assessment? This cannot be undone.`)) return;
    await runAssessmentAction(`${API_URL}/admin/assessments/${assessment.id}`, 'DELETE');
  };

  const duplicateQuestion = async (questionId, assessmentId) => {
    const target = assessments.find(a => a.id === assessmentId);
    try {
      const response = await fetchWithCredentials(`${API_URL}/admin/questions/${questionId}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({ assessment_id: assessmentId })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to copy question');
      }
      await loadQuestions();
      setQuestionNotice(`Question copied to "${target?.title ?? 'assessment'}".`);
      setTimeout(() => setQuestionNotice(''), 4000);
    } catch (error) {
      console.error('Error copying question:', error);
      setError(error.message);
    }
  };

  const toggleQuestion = async (questionId, enabled) => {
    try {
      const response = await fetchWithCredentials(`${API_URL}/questions/${questionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled })
      });

      if (!response.ok) throw new Error('Failed to update question');

      await loadQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
      setError('Failed to update question');
    }
  };

  const createQuestion = async () => {
    try {
      const { error: formError, payload } = buildQuestionPayload(newQuestion);
      if (formError) {
        setQuestionFormError(formError);
        return;
      }
      setQuestionFormError('');
      const questionData = { ...payload, assessment_id: selectedAssessmentId };

      const response = await fetchWithCredentials(`${API_URL}/admin/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(questionData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setQuestionFormError(errorData.error || 'Failed to create question');
        return;
      }

      setShowQuestionForm(false);
      setNewQuestion({
        text: '',
        type: 'multiple-choice',
        options: [],
        correct_answer: 0,
        points: 1,
        enabled: true
      });
      await loadQuestions();
    } catch (error) {
      console.error('Error creating question:', error);
      setError('Failed to create question');
    }
  };

  const updateQuestion = async (questionId, updates) => {
    try {
      const { error: formError, payload } = buildQuestionPayload(updates);
      if (formError) {
        setQuestionFormError(formError);
        return;
      }
      setQuestionFormError('');
      delete payload.assessment_id;
      const response = await fetchWithCredentials(`${API_URL}/questions/${questionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setQuestionFormError(errorData.error || 'Failed to update question');
        return;
      }

      await loadQuestions();
      setEditingQuestion(null);
      setShowQuestionForm(false);
      setQuestionFormError('');
    } catch (error) {
      console.error('Error updating question:', error);
      setError('Failed to update question');
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetchWithCredentials(`${API_URL}/questions/${questionId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete question');

      await loadQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      setError('Failed to delete question');
    }
  };


  const handleExportBackup = async () => {
    try {
      setIsExporting(true);
      setExportStatus('');

      const response = await fetchWithCredentials(`${API_URL}/export/backup`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export backup');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assessment-backup-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setExportStatus('Backup exported successfully!');
      setTimeout(() => setExportStatus(''), 3000);
    } catch (error) {
      console.error('Error exporting backup:', error);
      setExportStatus(error.message || 'Failed to export backup');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsExporting(true);
      setExportStatus('');

      const text = await file.text();
      const backup = JSON.parse(text);

      if (!backup.submissions || !backup.questions || !backup.settings) {
        throw new Error('Invalid backup file format');
      }

      if (!window.confirm('This will replace all existing data. Are you sure?')) {
        return;
      }

      const response = await fetchWithCredentials(`${API_URL}/import/backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backup)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to import backup');
      }

      const data = await response.json();
      setExportStatus(data.message || 'Backup imported successfully!');
      await loadSubmissions();
      await loadQuestions();
      await loadSettings();
      await loadAssessments();
      setTimeout(() => setExportStatus(''), 5000);
    } catch (error) {
      console.error('Error importing backup:', error);
      setExportStatus(error.message || 'Failed to import backup');
    } finally {
      setIsExporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  const loadSettings = async () => {
    // SECURITY: Only attempt to load if authenticated
    if (!isAdminAuth) {
      console.warn('Attempted to load settings without authentication');
      return;
    }
    
    try {
      const response = await fetchWithCredentials(`${API_URL}/settings`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        if (response.status === 401) {
          // Authentication failed - clear auth state
          setIsAdminAuth(false);
          setView('candidate');
          throw new Error('Authentication expired. Please log in again.');
        }
        throw new Error(errorData.error || errorData.details || `HTTP ${response.status}: Failed to load settings`);
      }
      const data = await response.json();
      setSettings(data);
      setAnthropicKeyMissing(!data.anthropicKeyConfigured);
      setSettingsError('');
    } catch (error) {
      console.error('Error loading settings:', error);
      setSettingsError(`Failed to load settings: ${error.message}`);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      setSettingsError('');
      setSettingsSaved(false);
      const response = await fetchWithCredentials(`${API_URL}/settings/${key}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ value })
      });

      if (!response.ok) throw new Error('Failed to update setting');

      await loadSettings();
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (error) {
      console.error('Error updating setting:', error);
      setSettingsError('Failed to update setting');
    }
  };

  const handleSubmit = async () => {
    console.log(answers);
    setIsGrading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          candidateInfo,
          answers,
          honeypot,
          formLoadedAt: formLoadedAtRef.current,
          assessmentSlug: currentAssessment?.slug
        })
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.message || errBody.error || 'Failed to submit assessment');
      }

      const submission = await response.json();
      setScore(submission.score);
      setSubmitted(true);
      setView('results');
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setError(error.message || 'Failed to submit assessment. Please try again.');
    } finally {
      setIsGrading(false);
    }
  };

  const handleAdminLogin = async () => {
    try {
      setError(null);
      const response = await fetchWithCredentials(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: adminPassword })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Login response received, verifying session...');
        
        // Small delay to ensure cookie is set before checking
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Verify session was created before setting auth state
        // Try multiple times in case of timing issues
        let authVerified = false;
        for (let attempt = 0; attempt < 3; attempt++) {
          const authCheck = await fetchWithCredentials(`${API_URL}/admin/check-auth`);
          if (authCheck.ok) {
            const authData = await authCheck.json();
            console.log('Auth check result:', authData);
            if (authData.isAuthenticated) {
              adminCsrfToken = authData.csrfToken || null;
              setIsAdminAuth(true);
              setView('admin');
              setError(null);
              setAdminPassword(''); // Clear password field
              console.log('Login successful, session established');
              authVerified = true;
              break;
            }
          }
          if (attempt < 2) {
            // Wait a bit longer before retry
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
        
        if (!authVerified) {
          setError('Session not properly established. Please check your browser settings allow cookies and try again.');
          console.warn('Session not properly established after login - check cookie settings');
          setIsAdminAuth(false);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Incorrect password' }));
        setError(errorData.error || 'Incorrect password');
        setIsAdminAuth(false);
      }
    } catch (error) {
      console.error('Error logging in:', error);
      setError(`Login failed: ${error.message}`);
      setIsAdminAuth(false);
    }
  };

  const markAsReviewed = async (submissionId) => {
    try {
      const response = await fetchWithCredentials(`${API_URL}/submissions/${submissionId}/review`, {
        method: 'PATCH'
      });

      if (!response.ok) throw new Error('Failed to mark as reviewed');

      await loadSubmissions();
      if (selectedSubmission?.id === submissionId) {
        const updatedSub = { ...selectedSubmission, reviewed: true };
        setSelectedSubmission(updatedSub);
      }
    } catch (error) {
      console.error('Error marking as reviewed:', error);
      setError('Failed to mark as reviewed');
    }
  };

  const allowRetake = async (submissionId) => {
    try {
      const response = await fetchWithCredentials(`${API_URL}/admin/submissions/${submissionId}/allow-retake`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to allow retake');

      await loadSubmissions();
      if (selectedSubmission?.id === submissionId) {
        setSelectedSubmission({ ...selectedSubmission, retakeAllowed: true });
      }
    } catch (error) {
      console.error('Error allowing retake:', error);
      setError('Failed to allow retake');
    }
  };

  const regradeSubmission = async (submissionId) => {
    setIsRegrading(true);
    try {
      const response = await fetchWithCredentials(`${API_URL}/admin/submissions/${submissionId}/regrade`, {
        method: 'POST'
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || 'Re-grade failed');
      }
      const updated = await response.json();
      setSelectedSubmission(updated);
      await loadSubmissions();
    } catch (err) {
      console.error('Error re-grading submission:', err);
      setError(`Re-grade failed: ${err.message}`);
    } finally {
      setIsRegrading(false);
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetchWithCredentials(`${API_URL}/submissions/${submissionId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete submission');

      await loadSubmissions();
      setError(null);
      setSelectedRowIds(prev => {
        if (!prev.has(submissionId)) return prev;
        const next = new Set(prev);
        next.delete(submissionId);
        return next;
      });
    } catch (error) {
      console.error('Error deleting submission:', error);
      setError('Failed to delete submission');
    }
  };

  const handleBulkMarkReviewed = async () => {
    const ids = Array.from(selectedRowIds);
    if (ids.length === 0) return;
    try {
      const results = await Promise.all(
        ids.map(id => fetchWithCredentials(`${API_URL}/submissions/${id}/review`, { method: 'PATCH' }))
      );
      const failedCount = results.filter(r => !r.ok).length;
      await loadSubmissions();
      setSelectedRowIds(new Set());
      setSelectionAnchor(null);
      if (failedCount > 0) {
        setError(`Failed to mark ${failedCount} of ${ids.length} submission(s) as reviewed`);
      } else {
        setError(null);
      }
    } catch (error) {
      console.error('Error bulk marking as reviewed:', error);
      setError('Failed to mark selected submissions as reviewed');
    }
  };

  const handleExportDownload = async () => {
    try {
      setIsExporting(true);
      setExportStatus('');

      const query = submissionAssessmentFilter === 'all' ? '' : `?assessmentId=${submissionAssessmentFilter}`;
      const response = await fetchWithCredentials(`${API_URL}/export/csv${query}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export CSV');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assessment-results-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setExportStatus('CSV downloaded successfully!');
      setTimeout(() => setExportStatus(''), 3000);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setExportStatus(error.message || 'Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportEmail = async () => {
    if (!exportEmail) {
      setExportStatus('Please enter an email address');
      return;
    }

    try {
      setIsExporting(true);
      setExportStatus('');

      const response = await fetchWithCredentials(`${API_URL}/export/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: exportEmail,
          ...(submissionAssessmentFilter === 'all' ? {} : { assessmentId: submissionAssessmentFilter })
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to email CSV');
      }

      const data = await response.json();
      setExportStatus(data.message || 'CSV emailed successfully!');
      setExportEmail('');
      setTimeout(() => setExportStatus(''), 5000);
    } catch (error) {
      console.error('Error emailing CSV:', error);
      setExportStatus(error.message || 'Failed to email CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const handleMultiSelect = (question, option) => {
    const current = answers[question] || [];
    const updated = current.includes(option)
      ? current.filter(o => o !== option)
      : [...current, option];
    setAnswers({ ...answers, [question]: updated });
  };

  // Get question text from backend-loaded questions list (by question ID key)
  const getQuestionTextFromList = (key, questionsList) => {
    if (!questionsList || !Array.isArray(questionsList)) return `Question ${key}`;
    const id = String(key).replace(/^q/, '');
    const question = questionsList.find(q => String(q.id) === id);
    return question?.text ?? `Question ${key}`;
  };

  // SECURITY: Admin view is protected - requires authentication
  // Even if someone navigates to /admin, they can only see the login form
  // All admin data requires server-side authentication via requireAuth middleware
  if (view === 'admin') {
    // Show login form if not authenticated
    if (!isAdminAuth) {
      return (
        <div className="min-h-screen bg-page-gradient p-8">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-invizio-ink mb-6">Admin Login</h2>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
                  {error}
                </div>
              )}
              <input
                type="password"
                placeholder="Enter admin password"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg mb-4 bg-white focus:ring-2 focus:ring-invizio focus:border-invizio"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAdminLogin();
                  }
                }}
              />
              <button
                onClick={handleAdminLogin}
                className="w-full bg-invizio text-white py-2 rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2"
              >
                Login
              </button>
              <button
                onClick={() => {
                  setView('candidate');
                  setError(null);
                }}
                className="w-full mt-2 text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 rounded"
              >
                Back to Assessment
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (selectedSubmission) {
      const emailKey = (selectedSubmission.candidateInfo?.email ?? '').trim().toLowerCase();
      // The retake rule is per assessment, so only compare submissions for the same one
      const isLatestForEmail = !submissions.some(s =>
        s.id !== selectedSubmission.id &&
        s.assessment?.id === selectedSubmission.assessment?.id &&
        (s.candidateInfo?.email ?? '').trim().toLowerCase() === emailKey &&
        new Date(s.submittedAt) > new Date(selectedSubmission.submittedAt)
      );
      const daysSinceSubmission = (Date.now() - new Date(selectedSubmission.submittedAt).getTime()) / 86400000;
      const showAllowRetake = isLatestForEmail && daysSinceSubmission < 30 && !selectedSubmission.retakeAllowed;

      return (
        <div className="min-h-screen bg-page-gradient p-8">
          <div className="max-w-5xl mx-auto">
            <button
              onClick={() => window.history.back()}
              className="mb-4 text-invizio hover:text-invizio-light"
            >
              ← Back to Submissions
            </button>

            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-invizio-ink">{selectedSubmission.candidateInfo?.fullName ?? ''}</h2>
                    {selectedSubmission.isRetake && (
                      <span className="px-2 py-0.5 bg-invizio-tint text-invizio-ink text-xs font-medium rounded-full">
                        Retake
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600">{selectedSubmission.candidateInfo?.email ?? ''}</p>
                  {selectedSubmission.assessment && (
                    <p className="text-sm text-gray-700">Assessment: {selectedSubmission.assessment.title}</p>
                  )}
                  <p className="text-sm text-gray-500">Submitted: {new Date(selectedSubmission.submittedAt).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-invizio-ink">
                    {selectedSubmission.score.percentage}%
                  </div>
                  <p className="text-sm text-gray-600">
                    {selectedSubmission.score.earnedPoints} / {selectedSubmission.score.totalPoints} points
                  </p>
                  {!selectedSubmission.reviewed && (
                    <button
                      onClick={() => markAsReviewed(selectedSubmission.id)}
                      className="mt-2 px-4 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark text-sm focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2"
                    >
                      Mark as Reviewed
                    </button>
                  )}
                  <button
                    onClick={() => regradeSubmission(selectedSubmission.id)}
                    disabled={isRegrading}
                    className="mt-2 ml-2 px-4 py-2 bg-invizio-ink text-white rounded-lg text-sm font-medium hover:bg-opacity-90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-invizio-ink focus:ring-offset-2"
                  >
                    {isRegrading ? 'Re-grading...' : 'Re-grade'}
                  </button>
                  {showAllowRetake && (
                    <button
                      onClick={() => allowRetake(selectedSubmission.id)}
                      className="mt-2 ml-2 px-4 py-2 bg-white text-invizio-ink border border-invizio rounded-lg text-sm font-medium hover:bg-invizio-tint focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2"
                    >
                      Allow Retake
                    </button>
                  )}
                  {selectedSubmission.retakeAllowed && (
                    <p className="mt-2 text-xs text-gray-500">Retake allowed — candidate may resubmit</p>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                {Object.entries(selectedSubmission.answers).map(([key, answer]) => {
                  const detail = selectedSubmission.score.detailed[key];
                  const shortAnswerGrades = selectedSubmission.score.shortAnswerGrades || {};
                  const isCorrect = detail?.correct;
                  const isShortAnswer = detail?.score !== undefined;

                  return (
                    <div key={key} className="border-b border-gray-200 pb-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-invizio-ink whitespace-pre-line">{getQuestionTextFromList(key, questions)}</h3>
                        {isShortAnswer ? (
                          <span className="text-sm font-medium text-gray-800 ml-4 whitespace-nowrap">
                            {detail.score}/100 ({detail.points.toFixed(1)}/{detail.total} pts)
                          </span>
                        ) : (
                          <span className={`text-sm font-medium ml-4 whitespace-nowrap ${isCorrect ? 'text-gray-700' : 'text-red-700'}`}>
                            {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                            {detail != null && typeof detail.points === 'number' && typeof detail.total === 'number' && (
                              <span className="text-gray-600 font-normal"> ({Number(detail.points).toFixed(1)}/{detail.total} pts)</span>
                            )}
                          </span>
                        )}
                      </div>
                      {(() => {
                        const question = questions.find(q => String(q.id) === String(key));
                        if (question?.type === 'matching' && Array.isArray(answer)) {
                          const matches = String(question.correct_answer ?? '').split('\n');
                          return (
                            <div className="text-gray-700 mb-2">
                              <strong>Answer:</strong>
                              <ul className="list-disc list-inside text-sm mt-1">
                                {(question.options || []).map((item, i) => (
                                  <li key={i}>
                                    {item} → {answer[i] || <em>(no answer)</em>}
                                    {answer[i] !== matches[i] && <span className="text-red-700"> (correct: {matches[i]})</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        }
                        return (
                          <p className="text-gray-700 mb-2">
                            <strong>Answer:</strong> {Array.isArray(answer) ? answer.join(question?.type === 'ordering' ? ' → ' : ', ') : answer}
                          </p>
                        );
                      })()}
                      {isShortAnswer && (
                        (shortAnswerGrades[key]?.gradingError || detail.feedback === 'Error grading answer') ? (
                          <p className="text-sm text-red-800 bg-red-50 border border-red-200 p-2 rounded" role="alert">
                            <strong>Grading error:</strong> {shortAnswerGrades[key]?.gradingError || 'Grading failed — click Re-grade to retry'}
                          </p>
                        ) : detail.feedback ? (
                          <p className="text-sm text-green-800 bg-green-50 border border-green-200 p-2 rounded">
                            <strong>Feedback:</strong> {detail.feedback}
                          </p>
                        ) : null
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    const assessmentQuestions = questions.filter(q => q.assessment_id === selectedAssessmentId);

    // SECURITY CHECK: Double-check authentication before rendering admin content
    // This prevents any edge cases where content might render without auth
    if (!isAdminAuth) {
      return (
        <div className="min-h-screen bg-page-gradient p-8">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-invizio-ink mb-6">Admin Login</h2>
              <p className="text-gray-600 mb-4">Authentication required to access admin panel.</p>
              <button
                onClick={() => setView('candidate')}
                className="w-full mt-2 text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 rounded"
              >
                Back to Assessment
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-page-gradient flex flex-col">
      <div className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <img src="https://mt.invizio.com/images/invizio_logo_sq.png" width="48" height="48" alt="Invizio" />
              <div>
                <h1 className="text-3xl font-bold text-invizio-ink">Technical Assessment System</h1>
                <p className="text-sm text-gray-500">Admin Panel</p>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  await fetchWithCredentials(`${API_URL}/admin/logout`, {
                    method: 'POST'
                  });
                } catch (error) {
                  console.error('Logout error:', error);
                }
                adminCsrfToken = null;
                setIsAdminAuth(false);
                setView('candidate');
                setAdminPassword('');
                setSubmissions([]);
                setQuestions([]);
                setSettings({});
                // Redirect away from admin route
                window.history.pushState({}, '', '/');
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Logout
            </button>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setAdminView('submissions')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  adminView === 'submissions'
                    ? 'border-invizio text-invizio'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Submissions
              </button>
              <button
                onClick={() => { setAdminView('assessments'); loadAssessments(); }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  adminView === 'assessments'
                    ? 'border-invizio text-invizio'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Assessments
              </button>
              <button
                onClick={() => setAdminView('questions')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  adminView === 'questions'
                    ? 'border-invizio text-invizio'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Manage Questions
              </button>
              <button
                onClick={() => setAdminView('settings')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  adminView === 'settings'
                    ? 'border-invizio text-invizio'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Settings
              </button>
            </nav>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
              {error}
            </div>
          )}

          {anthropicKeyMissing && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm" role="alert">
              <strong>Warning:</strong> ANTHROPIC_API_KEY is not configured. Short-answer questions cannot be AI-graded until this environment variable is set on the server.
            </div>
          )}

          {adminView === 'submissions' && (
            <>
              {/* Export Section */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <h2 className="text-xl font-bold text-invizio-ink mb-4">Export Results</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Export assessment results to CSV format. The export includes candidate information, assessment, scores, submission dates, and short answer responses, and follows the Assessment filter below.
                </p>

                {exportStatus && (
                  <div className={`mb-4 p-3 rounded-lg text-sm ${
                    exportStatus.includes('success') || exportStatus.includes('sent')
                      ? 'bg-gray-100 border border-gray-200 text-gray-800'
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                    {exportStatus}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleExportDownload}
                    disabled={isExporting || submissions.length === 0}
                    className="px-6 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExporting ? 'Exporting...' : 'Download CSV'}
                  </button>

                  <div className="flex gap-2 flex-1">
                    <input
                      type="email"
                      placeholder="Email address for export"
                      value={exportEmail}
                      onChange={(e) => setExportEmail(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-invizio focus:border-invizio"
                      disabled={isExporting}
                    />
                    <button
                      onClick={handleExportEmail}
                      disabled={isExporting || submissions.length === 0 || !exportEmail}
                      className="px-6 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isExporting ? 'Sending...' : 'Email CSV'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <label htmlFor="submission-search" className="text-sm font-medium text-gray-700 mb-2 block">
                  Search Submissions
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
                  <input
                    id="submission-search"
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-invizio focus:border-invizio"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-invizio rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6 space-y-4">
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Assessment</div>
                  <div role="group" aria-label="Filter by assessment" className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: 'All Assessments' },
                      ...assessments.map(a => ({ value: a.id, label: a.title }))
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={submissionAssessmentFilter === opt.value}
                        onClick={() => setSubmissionAssessmentFilter(opt.value)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-1 ${
                          submissionAssessmentFilter === opt.value
                            ? 'bg-invizio text-white border-invizio'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Status</div>
                  <div role="group" aria-label="Filter by status" className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: 'All Statuses' },
                      { value: 'new', label: 'New' },
                      { value: 'reviewed', label: 'Reviewed' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={statusFilter === opt.value}
                        onClick={() => setStatusFilter(opt.value)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-1 ${
                          statusFilter === opt.value
                            ? 'bg-invizio text-white border-invizio'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Submitted</div>
                  <div role="group" aria-label="Filter by submission recency" className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: 'All Time' },
                      { value: '7', label: 'Last 7 Days' },
                      { value: '30', label: '8–30 Days' },
                      { value: '60', label: '31–60 Days' },
                      { value: '90', label: '61–90 Days' },
                      { value: 'older', label: 'Older than 90 Days' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={recencyFilter === opt.value}
                        onClick={() => setRecencyFilter(opt.value)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-1 ${
                          recencyFilter === opt.value
                            ? 'bg-invizio text-white border-invizio'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {(() => {
                const normalizedSearch = searchQuery.trim().toLowerCase();
                const filteredSubmissions = submissions.filter(sub => {
                  if (submissionAssessmentFilter !== 'all' && sub.assessment?.id !== submissionAssessmentFilter) return false;
                  if (statusFilter === 'new' && sub.reviewed) return false;
                  if (statusFilter === 'reviewed' && !sub.reviewed) return false;
                  if (recencyFilter !== 'all' && getRecencyBucket(sub.submittedAt) !== recencyFilter) return false;
                  if (normalizedSearch) {
                    const fullName = (sub.candidateInfo?.fullName ?? '').toLowerCase();
                    const email = (sub.candidateInfo?.email ?? '').toLowerCase();
                    if (!fullName.includes(normalizedSearch) && !email.includes(normalizedSearch)) return false;
                  }
                  return true;
                });
                const emptyMessage = submissions.length === 0
                  ? 'No submissions yet'
                  : normalizedSearch
                    ? 'No submissions match your search'
                    : 'No submissions match the selected filters';
                const openSubmission = (sub) => {
                  submissionListScrollY.current = window.scrollY;
                  window.history.pushState({ submissionView: sub.id }, '', window.location.pathname);
                  setSelectedSubmission(sub);
                };
                const handleRowClick = (sub, e) => {
                  const index = filteredSubmissions.findIndex(s => s.id === sub.id);
                  if (e.shiftKey && selectionAnchor != null) {
                    const anchorIndex = filteredSubmissions.findIndex(s => s.id === selectionAnchor);
                    if (anchorIndex !== -1) {
                      const [start, end] = anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex];
                      const rangeIds = filteredSubmissions.slice(start, end + 1).map(s => s.id);
                      setSelectedRowIds(prev => new Set([...prev, ...rangeIds]));
                      return;
                    }
                  }
                  setSelectedRowIds(prev => {
                    const next = new Set(prev);
                    next.has(sub.id) ? next.delete(sub.id) : next.add(sub.id);
                    return next;
                  });
                  setSelectionAnchor(sub.id);
                };

                return (
                  <>
                    {selectedRowIds.size > 0 && (
                      <div className="bg-invizio-tint border border-invizio-light rounded-lg p-3 mb-4 flex items-center justify-between">
                        <span className="text-sm text-invizio-ink font-medium">{selectedRowIds.size} selected</span>
                        <div className="flex gap-4">
                          <button
                            onClick={() => { setSelectedRowIds(new Set()); setSelectionAnchor(null); }}
                            className="text-sm text-gray-600 hover:text-gray-800"
                          >
                            Clear
                          </button>
                          <button
                            onClick={handleBulkMarkReviewed}
                            className="px-4 py-1.5 bg-invizio text-white rounded-lg hover:bg-invizio-dark text-sm font-medium"
                          >
                            Mark Selected as Reviewed
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Submissions Table (md and up) */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden hidden md:block">
                      <table className="w-full">
                        <thead className="bg-gray-100 border-b">
                          <tr>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Candidate</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Assessment</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Score</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Submitted</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSubmissions.map((sub) => {
                            const pct = parseFloat(sub.score.percentage);
                            return (
                              <tr
                                key={sub.id}
                                onClick={(e) => handleRowClick(sub, e)}
                                className={`border-b border-gray-200 hover:bg-gray-50 cursor-pointer select-none ${!sub.reviewed ? 'bg-gray-50' : ''} ${selectedRowIds.has(sub.id) ? 'bg-invizio-tint ring-1 ring-inset ring-invizio' : ''}`}
                              >
                                <td className="px-6 py-4 text-sm text-gray-800">
                                  {sub.candidateInfo?.fullName ?? ''}
                                  {sub.isRetake && (
                                    <span className="ml-2 px-2 py-0.5 bg-invizio-tint text-invizio-ink text-xs font-medium rounded-full">
                                      Retake
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">{sub.candidateInfo?.email ?? ''}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{sub.assessment?.title ?? ''}</td>
                                <td className="px-6 py-4 text-sm font-semibold">
                                  <span className={pct > 80 ? 'text-green-700' : 'text-gray-800'}>
                                    {sub.score.percentage}%
                                  </span>
                                  {pct > 90 && (
                                    <Star className="inline-block w-4 h-4 ml-1 text-yellow-500 fill-yellow-400" aria-label="Top score" />
                                  )}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">
                                  {new Date(sub.submittedAt).toLocaleString()}
                                </td>
                                <td className="px-6 py-4">
                                  {sub.reviewed ? (
                                    <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                                      Reviewed
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                      New
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex gap-8">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); openSubmission(sub); }}
                                      className="text-invizio hover:text-invizio-dark text-sm font-medium focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-1 rounded"
                                    >
                                      View
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); deleteSubmission(sub.id); }}
                                      className="text-gray-700 hover:text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 rounded"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {filteredSubmissions.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                          {emptyMessage}
                        </div>
                      )}
                    </div>

                    {/* Submissions Cards (below md) */}
                    <div className="md:hidden space-y-4">
                      {filteredSubmissions.map((sub) => {
                        const pct = parseFloat(sub.score.percentage);
                        return (
                          <div
                            key={sub.id}
                            onClick={(e) => handleRowClick(sub, e)}
                            className={`bg-white rounded-lg shadow-sm p-4 cursor-pointer select-none ${!sub.reviewed ? 'border-l-4 border-invizio' : ''} ${selectedRowIds.has(sub.id) ? 'bg-invizio-tint ring-1 ring-inset ring-invizio' : ''}`}
                          >
                            <div className="flex justify-between items-start mb-2 gap-2">
                              <div className="min-w-0">
                                <div className="font-semibold text-invizio-ink truncate">
                                  {sub.candidateInfo?.fullName ?? ''}
                                  {sub.isRetake && (
                                    <span className="ml-2 px-2 py-0.5 bg-invizio-tint text-invizio-ink text-xs font-medium rounded-full">
                                      Retake
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600 break-all">{sub.candidateInfo?.email ?? ''}</div>
                                {sub.assessment && (
                                  <div className="text-xs text-gray-500">{sub.assessment.title}</div>
                                )}
                              </div>
                              {sub.reviewed ? (
                                <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full whitespace-nowrap">
                                  Reviewed
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full whitespace-nowrap">
                                  New
                                </span>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-sm mb-3">
                              <span className="text-gray-600">{new Date(sub.submittedAt).toLocaleString()}</span>
                              <span className="font-semibold">
                                <span className={pct > 80 ? 'text-green-700' : 'text-gray-800'}>
                                  {sub.score.percentage}%
                                </span>
                                {pct > 90 && (
                                  <Star className="inline-block w-4 h-4 ml-1 text-yellow-500 fill-yellow-400" aria-label="Top score" />
                                )}
                              </span>
                            </div>
                            <div className="flex gap-6 pt-2 border-t border-gray-100">
                              <button
                                onClick={(e) => { e.stopPropagation(); openSubmission(sub); }}
                                className="text-invizio hover:text-invizio-dark text-sm font-medium focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-1 rounded"
                              >
                                View
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteSubmission(sub.id); }}
                                className="text-gray-700 hover:text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 rounded"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {filteredSubmissions.length === 0 && (
                        <div className="bg-white rounded-lg shadow-sm text-center py-12 text-gray-500">
                          {emptyMessage}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {adminView === 'assessments' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-invizio-ink">Assessments</h2>
                  <button
                    onClick={() => {
                      setAssessmentFormError('');
                      setAssessmentForm(assessmentForm && !assessmentForm.id ? null : { slug: '', title: '', intro: '' });
                    }}
                    className="px-4 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 font-medium"
                  >
                    {assessmentForm && !assessmentForm.id ? 'Cancel' : '+ Add Assessment'}
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Each assessment has its own link and questions. Candidates who open the site without a link path get the default assessment. Deactivated assessments show candidates an &ldquo;assessment not available&rdquo; page.
                </p>

                {assessmentForm && (
                  <div className="border border-gray-200 rounded-lg p-6 mb-6 bg-gray-50">
                    <h3 className="text-lg font-semibold text-invizio-ink mb-4">
                      {assessmentForm.id ? 'Edit assessment' : 'New assessment'}
                    </h3>
                    <label htmlFor="assessment-slug" className="block text-sm font-medium text-gray-700 mb-1">
                      Link <span className="text-gray-500" aria-label="required">*</span>
                    </label>
                    <div className="flex items-center mb-1">
                      <span className="px-3 py-2 text-sm text-gray-500 bg-gray-100 border border-r-0 border-gray-200 rounded-l-lg">{window.location.origin}/</span>
                      <input
                        id="assessment-slug"
                        type="text"
                        placeholder="ae"
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-r-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio"
                        value={assessmentForm.slug}
                        onChange={(e) => setAssessmentForm({ ...assessmentForm, slug: e.target.value })}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mb-4">2–32 lowercase letters, digits, or hyphens.</p>

                    <label htmlFor="assessment-title" className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-gray-500" aria-label="required">*</span>
                    </label>
                    <input
                      id="assessment-title"
                      type="text"
                      placeholder="e.g. Automation Engineer Assessment"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio mb-4"
                      value={assessmentForm.title}
                      onChange={(e) => setAssessmentForm({ ...assessmentForm, title: e.target.value })}
                    />

                    <label htmlFor="assessment-intro" className="block text-sm font-medium text-gray-700 mb-1">
                      Landing page intro
                    </label>
                    <textarea
                      id="assessment-intro"
                      rows={6}
                      placeholder="What's evaluated, guidelines, and anything else candidates should read before starting."
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio mb-1"
                      value={assessmentForm.intro}
                      onChange={(e) => setAssessmentForm({ ...assessmentForm, intro: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mb-4">Line breaks are kept. Wrap text in **double asterisks** to make it bold.</p>

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={saveAssessment}
                        className="px-6 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 font-medium"
                      >
                        {assessmentForm.id ? 'Save changes' : 'Create assessment'}
                      </button>
                      <button
                        onClick={() => { setAssessmentForm(null); setAssessmentFormError(''); }}
                        className="px-6 py-2 border border-gray-200 text-gray-700 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 font-medium"
                      >
                        Cancel
                      </button>
                      {assessmentFormError && (
                        <span className="p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
                          {assessmentFormError}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {assessments.map(a => {
                    const url = `${window.location.origin}/${a.slug}`;
                    const deleteBlockedReason = a.isDefault
                      ? 'The default assessment cannot be deleted'
                      : (a.questionCount > 0 || a.submissionCount > 0)
                        ? 'Assessments with questions or submissions cannot be deleted. Deactivate it instead.'
                        : '';
                    return (
                      <div key={a.id} className={`p-4 border rounded-lg ${a.active ? 'bg-white' : 'bg-gray-50'}`}>
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className="font-semibold text-invizio-ink">{a.title}</span>
                              {a.isDefault && (
                                <span className="px-2 py-0.5 bg-invizio-tint text-invizio-ink text-xs font-medium rounded-full">Default</span>
                              )}
                              {!a.active && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-full">Inactive</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-600 break-all">{url}</span>
                              <button
                                type="button"
                                onClick={() => navigator.clipboard?.writeText(url)}
                                className="text-invizio hover:text-invizio-dark text-xs font-medium focus:outline-none focus:ring-2 focus:ring-invizio rounded"
                              >
                                Copy
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {a.questionCount} {a.questionCount === 1 ? 'question' : 'questions'} · {a.submissionCount} {a.submissionCount === 1 ? 'submission' : 'submissions'}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 md:justify-end">
                            {!a.isDefault && a.active && (
                              <button
                                onClick={() => runAssessmentAction(`${API_URL}/admin/assessments/${a.id}/default`, 'POST')}
                                className="px-3 py-1 border border-invizio text-invizio-ink rounded text-xs hover:bg-invizio-tint focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-1"
                              >
                                Make default
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setAssessmentFormError('');
                                setAssessmentForm({ id: a.id, slug: a.slug, title: a.title, intro: a.intro, originalSlug: a.slug });
                              }}
                              className="px-3 py-1 bg-invizio text-white rounded text-xs hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-1"
                            >
                              Edit
                            </button>
                            <span title={deleteBlockedReason}>
                              <button
                                onClick={() => deleteAssessment(a)}
                                disabled={!!deleteBlockedReason}
                                className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Delete
                              </button>
                            </span>
                            <label
                              className={`relative inline-flex items-center ${a.isDefault ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                              title={a.isDefault ? 'Make another assessment the default before deactivating this one' : (a.active ? 'Deactivate' : 'Activate')}
                            >
                              <input
                                type="checkbox"
                                aria-label={`${a.title} active`}
                                checked={a.active}
                                disabled={a.isDefault}
                                onChange={(e) => runAssessmentAction(`${API_URL}/admin/assessments/${a.id}`, 'PATCH', { active: e.target.checked })}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-invizio/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-invizio"></div>
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {adminView === 'questions' && (
            <div className="space-y-6">
              {/* Import/Export Section */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold text-invizio-ink mb-4">Database Backup & Restore</h3>
                {exportStatus && (
                  <div className={`mb-4 p-3 rounded-lg text-sm ${
                    exportStatus.includes('success') || exportStatus.includes('imported')
                      ? 'bg-gray-100 border border-gray-200 text-gray-800'
                      : 'bg-red-50 border border-red-200 text-red-700'
                  }`}>
                    {exportStatus}
                  </div>
                )}
                <div className="flex gap-4">
                  <button
                    onClick={handleExportBackup}
                    disabled={isExporting}
                    className="px-6 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExporting ? 'Exporting...' : 'Export Backup (JSON)'}
                  </button>
                  <label className="px-6 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    {isExporting ? 'Importing...' : 'Import Backup'}
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportBackup}
                      disabled={isExporting}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Question Management: heading + form (scroll target when form opens) */}
              <div ref={questionFormSectionRef} className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-invizio-ink">Question Management</h2>
                  <div className="flex items-center gap-2 ml-auto mr-4">
                    <label htmlFor="admin-question-assessment" className="text-sm font-medium text-gray-700">Assessment</label>
                    <select
                      id="admin-question-assessment"
                      className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio"
                      value={selectedAssessmentId ?? ''}
                      onChange={(e) => {
                        setSelectedAssessmentId(Number(e.target.value));
                        setShowQuestionForm(false);
                        setEditingQuestion(null);
                      }}
                    >
                      {assessments.map(a => (
                        <option key={a.id} value={a.id}>{a.title} (/{a.slug})</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => {
                      setShowQuestionForm(!showQuestionForm);
                      setEditingQuestion(null);
                    }}
                    className="px-4 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 font-medium"
                  >
                    {showQuestionForm ? 'Cancel' : '+ Add Question'}
                  </button>
                </div>

                {/* Question Form — sectioned, reduced density, primary CTA only on save */}
                {showQuestionForm && (() => {
                const qType = editingQuestion ? editingQuestion.type : newQuestion.type;
                const optionsList = editingQuestion ? (editingQuestion.options || []) : newQuestion.options || [];
                const rawCorrectAnswer = editingQuestion ? editingQuestion.correct_answer : newQuestion.correct_answer;
                // Choice types use index (multiple-choice) or indices array (multiple-select) in form state
                const correctAnswer = rawCorrectAnswer;
                const isChoice = qType === 'multiple-choice' || qType === 'multiple-select';
                const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
                return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
                  <h3 className="text-lg font-semibold text-invizio-ink mb-8">
                    {editingQuestion
                      ? 'Edit question'
                      : `Create new question in "${assessments.find(a => a.id === selectedAssessmentId)?.title ?? ''}"`}
                  </h3>

                  {/* Section: Question + Answer type */}
                  <section className="mb-10">
                    <label htmlFor="admin-question-text" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Question text <span className="text-gray-500" aria-label="required">*</span>
                    </label>
                    <textarea
                      id="admin-question-text"
                      className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-lg bg-gray-50 focus:ring-1 focus:ring-invizio focus:border-invizio focus:bg-white max-h-[100px]"
                      rows={5}
                      value={editingQuestion ? editingQuestion.text : newQuestion.text}
                      onChange={(e) => editingQuestion 
                        ? setEditingQuestion({...editingQuestion, text: e.target.value})
                        : setNewQuestion({...newQuestion, text: e.target.value})
                      }
                    />
                    <div className="mt-4">
                      <label htmlFor="admin-question-type" className="block text-sm font-medium text-gray-700">
                        Answer type <span className="text-gray-500" aria-label="required">*</span>
                      </label>
                      <select
                        id="admin-question-type"
                        className="w-full max-w-xs px-4 py-3 min-h-[48px] text-base border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio"
                        value={qType}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          const current = editingQuestion || newQuestion;
                          const setCurrent = editingQuestion ? setEditingQuestion : setNewQuestion;
                          if (['fill-in', 'ordering', 'matching'].includes(nextType)) {
                            const keptOptions = Array.isArray(current.options) ? current.options : [];
                            setCurrent({
                              ...current,
                              type: nextType,
                              options: nextType === 'fill-in' ? [] : keptOptions,
                              correct_answer: nextType === 'fill-in' ? '' : null,
                              matches: nextType === 'matching' ? keptOptions.map(() => '') : undefined
                            });
                            return;
                          }
                          const opts = nextType === 'short-answer'
                            ? null
                            : (editingQuestion ? editingQuestion.options : newQuestion.options) || [];
                          const hasOptions = Array.isArray(opts) && opts.length > 0;
                          const defaultCorrect = nextType === 'multiple-choice' && hasOptions ? 0
                            : nextType === 'multiple-select' && hasOptions ? [0]
                            : nextType === 'multiple-select' ? [] : (editingQuestion ? editingQuestion.correct_answer : newQuestion.correct_answer);
                          editingQuestion
                            ? setEditingQuestion({
                                ...editingQuestion,
                                type: nextType,
                                options: nextType === 'short-answer' ? null : editingQuestion.options,
                                correct_answer: defaultCorrect
                              })
                            : setNewQuestion({
                                ...newQuestion,
                                type: nextType,
                                options: nextType === 'short-answer' ? [] : newQuestion.options,
                                correct_answer: defaultCorrect
                              });
                        }}
                      >
                        <option value="multiple-choice">Multiple choice</option>
                        <option value="multiple-select">Multiple select</option>
                        <option value="short-answer">Short answer</option>
                        <option value="fill-in">Fill in (exact match)</option>
                        <option value="ordering">Ordering</option>
                        <option value="matching">Matching</option>
                      </select>
                    </div>
                  </section>

                  {/* Section: Options + Correct answer (choice/select), or items in order (ordering) */}
                  {(isChoice || qType === 'ordering') && (
                    <section className="mb-10">
                      <h4 className="text-sm font-semibold text-invizio-ink pb-2 border-b border-gray-200 mb-3">{qType === 'ordering' ? 'Items' : 'Options'}</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        {qType === 'ordering'
                          ? 'List the items in the correct order; use the arrows to reorder. Candidates see them shuffled.'
                          : 'Select the correct answer(s) with the radio or checkbox next to each option.'}
                      </p>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                        {optionsList.map((opt, idx) => {
                          const letter = qType === 'ordering' ? String(idx + 1) : (optionLetters[idx] ?? String(idx + 1));
                          const isCorrect = qType === 'multiple-select'
                            ? (Array.isArray(correctAnswer) && correctAnswer.includes(idx))
                            : (typeof correctAnswer === 'number' && correctAnswer === idx);
                          return (
                            <div key={idx} className="flex items-center gap-3">
                              <span className="w-6 text-sm font-medium text-gray-500 shrink-0" aria-hidden="true">{letter}.</span>
                              <input
                                type="text"
                                className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio min-h-[44px]"
                                value={opt}
                                onChange={(e) => {
                                  const options = [...optionsList];
                                  options[idx] = e.target.value;
                                  editingQuestion
                                    ? setEditingQuestion({...editingQuestion, options})
                                    : setNewQuestion({...newQuestion, options});
                                }}
                              />
                              {qType === 'ordering' ? (
                                <div className="flex shrink-0">
                                  {[[-1, '↑', 'up'], [1, '↓', 'down']].map(([delta, arrow, dir]) => (
                                    <button
                                      key={dir}
                                      type="button"
                                      disabled={idx + delta < 0 || idx + delta >= optionsList.length}
                                      onClick={() => {
                                        const options = [...optionsList];
                                        [options[idx], options[idx + delta]] = [options[idx + delta], options[idx]];
                                        editingQuestion
                                          ? setEditingQuestion({...editingQuestion, options})
                                          : setNewQuestion({...newQuestion, options});
                                      }}
                                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 focus:outline-none focus:ring-1 focus:ring-gray-400"
                                      aria-label={`Move item ${idx + 1} ${dir}`}
                                    >
                                      {arrow}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                              <label className="flex items-center gap-1.5 shrink-0 text-sm text-gray-600 cursor-pointer">
                                {qType === 'multiple-select' ? (
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4 rounded border-gray-300 text-invizio focus:ring-1 focus:ring-invizio"
                                    checked={isCorrect}
                                    onChange={() => {
                                      const current = Array.isArray(correctAnswer) ? [...correctAnswer] : [];
                                      const next = current.includes(idx) ? current.filter((i) => i !== idx).sort((a, b) => a - b) : [...current, idx].sort((a, b) => a - b);
                                      editingQuestion
                                        ? setEditingQuestion({...editingQuestion, correct_answer: next})
                                        : setNewQuestion({...newQuestion, correct_answer: next});
                                    }}
                                  />
                                ) : (
                                  <input
                                    type="radio"
                                    name="admin-correct-answer"
                                    className="w-4 h-4 border-gray-300 text-invizio focus:ring-1 focus:ring-invizio"
                                    checked={isCorrect}
                                    onChange={() => editingQuestion
                                      ? setEditingQuestion({...editingQuestion, correct_answer: idx})
                                      : setNewQuestion({...newQuestion, correct_answer: idx})
                                    }
                                  />
                                )}
                                <span>Correct</span>
                              </label>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const options = optionsList.filter((_, i) => i !== idx);
                                  if (qType === 'ordering') {
                                    editingQuestion
                                      ? setEditingQuestion({...editingQuestion, options})
                                      : setNewQuestion({...newQuestion, options});
                                    return;
                                  }
                                  const newCorrect = qType === 'multiple-select' && Array.isArray(correctAnswer)
                                    ? correctAnswer.filter((i) => i !== idx).map((i) => i > idx ? i - 1 : i).sort((a, b) => a - b)
                                    : (correctAnswer === idx ? 0 : (correctAnswer > idx ? correctAnswer - 1 : correctAnswer));
                                  editingQuestion
                                    ? setEditingQuestion({...editingQuestion, options, correct_answer: newCorrect})
                                    : setNewQuestion({...newQuestion, options, correct_answer: newCorrect});
                                }}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                                aria-label={`Remove ${qType === 'ordering' ? 'item' : 'option'} ${letter}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                        <div className="flex items-center gap-2 pt-2">
                          <input
                            id="admin-new-option"
                            type="text"
                            className="flex-1 max-w-md px-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio min-h-[44px]"
                            placeholder={qType === 'ordering' ? 'Next item' : 'New option text'}
                            value={newOption}
                            onChange={(e) => setNewOption(e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newOption.trim() && qType === 'ordering') {
                                const options = [...optionsList, newOption];
                                editingQuestion
                                  ? setEditingQuestion({...editingQuestion, options})
                                  : setNewQuestion({...newQuestion, options});
                                setNewOption('');
                              } else if (newOption.trim()) {
                                const options = [...optionsList, newOption];
                                const isFirstOption = optionsList.length === 0;
                                const defaultCorrect = isFirstOption
                                  ? (qType === 'multiple-select' ? [0] : 0)
                                  : (editingQuestion ? editingQuestion.correct_answer : newQuestion.correct_answer);
                                editingQuestion
                                  ? setEditingQuestion({...editingQuestion, options, correct_answer: defaultCorrect})
                                  : setNewQuestion({...newQuestion, options, correct_answer: defaultCorrect});
                                setNewOption('');
                              }
                            }}
                            className="py-2.5 px-3 text-xs font-medium border border-gray-200 text-gray-600 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-400 shrink-0"
                          >
                            {qType === 'ordering' ? 'Add item' : 'Add option'}
                          </button>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* Section: Pairs (matching) - options hold the left side, matches the right */}
                  {qType === 'matching' && (() => {
                    const current = editingQuestion || newQuestion;
                    const setCurrent = editingQuestion ? setEditingQuestion : setNewQuestion;
                    const lefts = current.options || [];
                    const rights = current.matches || lefts.map(() => '');
                    const setPairs = (options, matches) => setCurrent({ ...current, options, matches });
                    return (
                      <section className="mb-10">
                        <h4 className="text-sm font-semibold text-invizio-ink pb-2 border-b border-gray-200 mb-3">Pairs</h4>
                        <p className="text-xs text-gray-500 mb-3">Enter each item with its correct match. Candidates pick a match for each item from the shuffled list of matches.</p>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                          {lefts.map((left, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <span className="w-6 text-sm font-medium text-gray-500 shrink-0" aria-hidden="true">{idx + 1}.</span>
                              <input
                                type="text"
                                aria-label={`Item ${idx + 1}`}
                                placeholder="Item"
                                className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio min-h-[44px]"
                                value={left}
                                onChange={(e) => setPairs(lefts.map((l, i) => i === idx ? e.target.value : l), rights)}
                              />
                              <span className="text-gray-400" aria-hidden="true">→</span>
                              <input
                                type="text"
                                aria-label={`Match for item ${idx + 1}`}
                                placeholder="Correct match"
                                className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio min-h-[44px]"
                                value={rights[idx] ?? ''}
                                onChange={(e) => setPairs(lefts, rights.map((r, i) => i === idx ? e.target.value : r))}
                              />
                              <button
                                type="button"
                                onClick={() => setPairs(lefts.filter((_, i) => i !== idx), rights.filter((_, i) => i !== idx))}
                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
                                aria-label={`Remove pair ${idx + 1}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setPairs([...lefts, ''], [...rights, ''])}
                            className="py-2.5 px-3 text-xs font-medium border border-gray-200 text-gray-600 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-400"
                          >
                            Add pair
                          </button>
                        </div>
                      </section>
                    );
                  })()}

                  {/* Section: Accepted answers (fill-in) */}
                  {qType === 'fill-in' && (
                    <section className="mb-10">
                      <h4 className="text-sm font-semibold text-invizio-ink pb-2 border-b border-gray-200 mb-4">Correct answer</h4>
                      <label htmlFor="admin-accepted-answers" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Accepted answers (one per line)
                      </label>
                      <textarea
                        id="admin-accepted-answers"
                        className="w-full px-4 py-3 text-base border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio min-h-[80px]"
                        rows={3}
                        value={editingQuestion ? (editingQuestion.correct_answer || '') : (newQuestion.correct_answer || '')}
                        onChange={(e) => editingQuestion
                          ? setEditingQuestion({...editingQuestion, correct_answer: e.target.value})
                          : setNewQuestion({...newQuestion, correct_answer: e.target.value})
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1.5">Matching ignores capitalization and extra spaces.</p>
                    </section>
                  )}

                  {/* Section: Correct answer (short-answer) */}
                  {qType === 'short-answer' && (
                    <section className="mb-10">
                      <h4 className="text-sm font-semibold text-invizio-ink pb-2 border-b border-gray-200 mb-4">Correct answer</h4>
                      <label htmlFor="admin-sample-answer" className="block text-sm font-medium text-gray-700 mb-1.5">
                        Sample answer (for grading)
                      </label>
                      <textarea
                        id="admin-sample-answer"
                        className="w-full px-4 py-3 text-base border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio min-h-[100px]"
                        rows={4}
                        value={editingQuestion ? (editingQuestion.correct_answer || '') : (newQuestion.correct_answer || '')}
                        onChange={(e) => editingQuestion
                          ? setEditingQuestion({...editingQuestion, correct_answer: e.target.value})
                          : setNewQuestion({...newQuestion, correct_answer: e.target.value})
                        }
                      />
                    </section>
                  )}

                  {/* Section: Points + Enabled */}
                  <section className="mb-10">
                    <h4 className="text-sm font-semibold text-invizio-ink pb-2 border-b border-gray-200 mb-4">Settings</h4>
                    <div className="flex flex-wrap gap-8 items-start">
                      <div className="w-24">
                        <label htmlFor="admin-question-points" className="block text-sm font-medium text-gray-700 mb-1.5">
                          Points <span className="text-gray-500" aria-label="required">*</span>
                        </label>
                        <input
                          id="admin-question-points"
                          type="number"
                          className="w-full px-4 py-3 text-base border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio max-h-[42px]"
                          min={1}
                          value={editingQuestion ? editingQuestion.points : newQuestion.points}
                          onChange={(e) => editingQuestion
                            ? setEditingQuestion({...editingQuestion, points: parseInt(e.target.value) || 1})
                            : setNewQuestion({...newQuestion, points: parseInt(e.target.value) || 1})
                          }
                        />
                      </div>
                      <div className="flex items-center pt-9">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-5 h-5 rounded border-gray-300 text-invizio focus:ring-1 focus:ring-invizio"
                            checked={editingQuestion ? editingQuestion.enabled : newQuestion.enabled}
                            onChange={(e) => editingQuestion
                              ? setEditingQuestion({...editingQuestion, enabled: e.target.checked})
                              : setNewQuestion({...newQuestion, enabled: e.target.checked})
                            }
                          />
                          <span className="text-sm text-gray-700">Enabled</span>
                        </label>
                      </div>
                    </div>
                  </section>

                  {/* Primary action only */}
                  <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={editingQuestion ? () => updateQuestion(editingQuestion.id, editingQuestion) : createQuestion}
                      className="px-6 py-3 min-h-[48px] text-base font-semibold bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-1 focus:ring-invizio focus:ring-offset-2"
                    >
                      {editingQuestion ? 'Update question' : 'Create question'}
                    </button>
                    {editingQuestion && (
                      <button
                        type="button"
                        onClick={() => { setEditingQuestion(null); setShowQuestionForm(false); }}
                        className="px-6 py-3 min-h-[48px] text-base font-medium border border-gray-200 text-gray-700 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:ring-offset-2"
                      >
                        Cancel
                      </button>
                    )}
                    {questionFormError && (
                      <span className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
                        {questionFormError}
                      </span>
                    )}
                  </div>
                </div>
                );
              })()}
              </div>

              {/* Questions List */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                  <p className="text-sm text-gray-600 mb-6">
                    Enable or disable questions, or edit/delete them. Questions are displayed in the order they were created. Use &ldquo;Copy to&rdquo; to reuse a question in another assessment.
                  </p>
                  {questionNotice && (
                    <div className="mb-4 p-3 rounded-lg text-sm bg-gray-100 border border-gray-200 text-gray-800" role="status">
                      {questionNotice}
                    </div>
                  )}
                  <div className="space-y-3">
                    {assessmentQuestions.map((question, index) => (
                      <div
                        key={question.id}
                        className={`p-4 border rounded-lg ${
                          question.enabled ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-xs text-gray-500">#{index + 1}</span>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded">
                                {QUESTION_TYPE_LABELS[question.type] ?? question.type}
                              </span>
                              <span className="text-xs px-2 py-1 bg-gray-200 text-gray-800 rounded">
                                {question.points} {question.points === 1 ? 'point' : 'points'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-800 mb-2 whitespace-pre-line">{question.text ?? ''}</p>
                            {(question.type === 'multiple-choice' || question.type === 'multiple-select') && question.options && question.options.length > 0 && (
                              <div className="mt-2 pl-4">
                                <p className="text-xs text-gray-500 mb-1">Options:</p>
                                <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                                  {question.options.map((opt, idx) => {
                                    const correctIndices = question.type === 'multiple-choice'
                                      ? (typeof question.correct_answer === 'string' && /^\d+$/.test(question.correct_answer) ? [parseInt(question.correct_answer, 10)] : [])
                                      : (typeof question.correct_answer === 'string' ? question.correct_answer.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n)) : []);
                                    const isCorrectOption = correctIndices.includes(idx);
                                    return (
                                      <li key={idx} className={isCorrectOption ? 'font-semibold text-gray-800' : ''}>
                                        {opt ?? ''}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}
                            {question.type === 'fill-in' && (
                              <p className="mt-2 pl-4 text-xs text-gray-600">
                                Accepted: <span className="font-semibold text-gray-800">{String(question.correct_answer ?? '').split('\n').join(' · ')}</span>
                              </p>
                            )}
                            {question.type === 'ordering' && Array.isArray(question.options) && (
                              <div className="mt-2 pl-4">
                                <p className="text-xs text-gray-500 mb-1">Correct order:</p>
                                <ol className="list-decimal list-inside text-xs text-gray-600 space-y-1">
                                  {question.options.map((opt, idx) => <li key={idx}>{opt}</li>)}
                                </ol>
                              </div>
                            )}
                            {question.type === 'matching' && Array.isArray(question.options) && (
                              <div className="mt-2 pl-4">
                                <p className="text-xs text-gray-500 mb-1">Pairs:</p>
                                <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                                  {question.options.map((opt, idx) => (
                                    <li key={idx}>{opt} → <span className="font-semibold text-gray-800">{String(question.correct_answer ?? '').split('\n')[idx]}</span></li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          <div className="ml-4 flex items-center gap-2">
                            {assessments.length > 1 && (
                              <select
                                aria-label="Copy question to another assessment"
                                className="px-2 py-1 text-xs border border-gray-200 rounded bg-white focus:ring-1 focus:ring-invizio focus:border-invizio"
                                value=""
                                onChange={(e) => duplicateQuestion(question.id, Number(e.target.value))}
                              >
                                <option value="" disabled>Copy to…</option>
                                {assessments.filter(a => a.id !== question.assessment_id).map(a => (
                                  <option key={a.id} value={a.id}>{a.title}</option>
                                ))}
                              </select>
                            )}
                            <button
                              onClick={() => {
                                setEditingQuestion(normalizeQuestionForEdit({ ...question }));
                                setShowQuestionForm(true);
                              }}
                              className="px-3 py-1 bg-invizio text-white rounded text-xs hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-1"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteQuestion(question.id)}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
                            >
                              Delete
                            </button>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={question.enabled}
                                onChange={(e) => toggleQuestion(question.id, e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-invizio/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-invizio"></div>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {assessmentQuestions.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No questions in this assessment yet. Create the first one above.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {adminView === 'settings' && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="text-xl font-bold text-invizio-ink mb-4">Settings</h2>
                <p className="text-sm text-gray-600 mb-6">
                  Configure email notifications and other system settings.
                </p>

                {settingsError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {settingsError}
                  </div>
                )}

                {settingsSaved && (
                  <div className="mb-4 p-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-800 text-sm">
                    Settings saved successfully!
                  </div>
                )}

                <div className="space-y-6">
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold text-invizio-ink mb-4">Slack Notifications</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Slack Webhook URL
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Enter your Slack webhook URL to receive notifications when a new assessment is submitted. 
                        Get your webhook URL from: <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener noreferrer" className="text-invizio underline hover:text-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-1 rounded">Slack API</a>
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="url"
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-invizio focus:border-invizio"
                          placeholder="https://hooks.slack.com/services/..."
                          value={settings.slack_webhook_url || ''}
                          onChange={(e) => setSettings({ ...settings, slack_webhook_url: e.target.value })}
                        />
                        <button
                          onClick={() => updateSetting('slack_webhook_url', settings.slack_webhook_url || '')}
                          className="px-6 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark font-medium"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold text-invizio-ink mb-4">Email Notifications</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notification Email Address
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Enter the email address where you want to receive notifications when a new assessment is submitted.
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="email"
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-invizio focus:border-invizio"
                          placeholder="notifications@example.com"
                          value={settings.notification_email || ''}
                          onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
                        />
                        <button
                          onClick={() => updateSetting('notification_email', settings.notification_email || '')}
                          className="px-6 py-2 bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 font-medium"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

        {/* Footer */}
        <footer className="w-full bg-gray-800 text-white py-3 mt-auto">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-sm">
              &copy; {new Date().getFullYear()} Invizio. All rights reserved.
              {' '}&middot;{' '}
              Build {process.env.REACT_APP_GIT_COMMIT || 'unknown'}
            </p>
          </div>
        </footer>
      </div>
    );
  }

  if (view === 'results') {
    return (
      <div className="min-h-screen bg-page-gradient flex flex-col">
        <div className="flex-1 p-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center mb-8">
                <CheckCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" aria-hidden="true" />
                <h2 className="text-3xl font-bold text-invizio-ink mb-2">Assessment complete</h2>
                <p className="text-gray-600">Thank you for completing the technical evaluation.</p>
              </div>

              {score && (
                <>
                  <div className="bg-invizio-tint border border-gray-200 rounded-lg p-8 text-center mb-8">
                    <p className="text-sm font-medium text-gray-500 mb-2">Your score</p>
                    <p className="text-5xl font-bold text-invizio-ink">{score.percentage}%</p>
                    <p className="text-sm text-gray-600 mt-2">{score.earnedPoints} out of {score.totalPoints} points</p>
                  </div>

                  <div className="space-y-4 mb-8">
                    <h3 className="font-semibold text-invizio-ink text-lg">Performance breakdown</h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-invizio-tint border border-gray-200 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">Correct</p>
                        <p className="text-2xl font-bold text-invizio-ink">
                          {Object.values(score.detailed).filter(d => d.correct === true).length}
                        </p>
                      </div>
                      <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">Incorrect</p>
                        <p className="text-2xl font-bold text-red-700">
                          {Object.values(score.detailed).filter(d => d.correct === false).length}
                        </p>
                      </div>
                    </div>

                    {score.shortAnswerGrades && Object.keys(score.shortAnswerGrades).length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-semibold text-invizio-ink mb-3">Short answer feedback</h4>
                        {Object.entries(score.shortAnswerGrades).map(([key, grade]) => (
                          <div key={key} className="bg-invizio-tint border border-gray-200 p-4 rounded-lg mb-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-gray-700 whitespace-pre-line">{getQuestionTextFromList(key, enabledQuestions)}</span>
                              <span className="text-invizio-ink font-semibold">{grade.score}/100</span>
                            </div>
                            <p className="text-sm text-gray-600">{grade.feedback}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              <p className="text-center text-gray-600">
                Your results have been saved and will be reviewed by the Invizio team.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="w-full bg-gray-800 text-white py-3 mt-auto">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-sm">
              &copy; {new Date().getFullYear()} Invizio. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  const pageFooter = (
    <footer className="w-full bg-gray-800 text-white py-3 mt-auto">
      <div className="max-w-6xl mx-auto px-4 text-center">
        <p className="text-sm">
          &copy; {new Date().getFullYear()} Invizio. All rights reserved.
        </p>
      </div>
    </footer>
  );

  if (view === 'unavailable') {
    return (
      <div className="min-h-screen bg-page-gradient flex flex-col">
        <div className="flex-1 p-4 sm:p-6 md:p-8 flex items-center justify-center">
          <div className="w-full max-w-md bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <img src="https://mt.invizio.com/images/invizio_logo_sq.png" width="64" height="64" alt="Invizio" className="mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-invizio-ink mb-3">Assessment not available</h1>
            <p className="text-sm text-gray-600">
              This assessment link isn&apos;t active. Check the link you were given, or contact the person who sent it to you.
            </p>
          </div>
        </div>
        {pageFooter}
      </div>
    );
  }

  if (view === 'candidate' && !submitted && !currentAssessment) {
    return (
      <div className="min-h-screen bg-page-gradient flex flex-col">
        <div className="flex-1 p-8 flex items-center justify-center">
          {error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">{error}</div>
          ) : (
            <p className="text-gray-500" aria-live="polite">Loading…</p>
          )}
        </div>
        {pageFooter}
      </div>
    );
  }

  if (view === 'candidate' && !submitted) {
    return (
      <div className="min-h-screen bg-page-gradient flex flex-col">
        {/* Admin access is hidden - only accessible via /admin URL path */}

        {/* Main Content — two panels; on mobile form first so action is primary */}
        <div className="flex-1 p-4 sm:p-6 md:p-8 flex items-center justify-center">
          <div className="w-full max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8 flex flex-col md:flex-row gap-8 md:gap-12">
              {/* Left: compact, structured — order-2 on mobile so form is above */}
              <div className="flex-1 text-left flex flex-col order-2 md:order-1 md:justify-center">
                <img src="https://mt.invizio.com/images/invizio_logo_sq.png" width="80" height="80" alt="Invizio" className="mb-2" />
                <h1 className="text-2xl md:text-3xl font-bold text-invizio-ink mb-6">{currentAssessment.title}</h1>

                <IntroText text={currentAssessment.intro} />
              </div>

              {/* Right: form as primary focus — no nested box, flat structure; order-1 on mobile */}
              <div className="flex-1 flex flex-col justify-center order-1 md:order-2 max-w-md md:max-w-none">
                <label htmlFor="candidate-full-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-gray-500" aria-label="required">*</span>
                </label>
                <input
                  id="candidate-full-name"
                  type="text"
                  placeholder="e.g. Jane Smith"
                  className="w-full px-4 py-3 min-h-[48px] text-base border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-invizio focus:border-invizio mb-4"
                  value={candidateInfo.fullName}
                  onChange={(e) => setCandidateInfo({ ...candidateInfo, fullName: e.target.value })}
                />

                <label htmlFor="candidate-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-gray-500" aria-label="required">*</span>
                </label>
                <input
                  id="candidate-email"
                  type="email"
                  placeholder="e.g. jane.smith@example.com"
                  className="w-full px-4 py-3 min-h-[48px] text-base border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-invizio focus:border-invizio mb-4"
                  value={candidateInfo.email}
                  onChange={(e) => setCandidateInfo({ ...candidateInfo, email: e.target.value })}
                />

                {/* Honeypot: hidden from real users; bots that auto-fill every field trip this */}
                <div
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                  aria-hidden="true"
                >
                  <label htmlFor="candidate-company">Company</label>
                  <input
                    id="candidate-company"
                    name="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>

                <p className="text-xs text-gray-500 mb-4" aria-hidden="true">
                  For your records: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>


                <button
                  onClick={async () => {
                    if (!candidateInfo.fullName?.trim() || !candidateInfo.email?.trim()) {
                      if (!candidateInfo.fullName?.trim() && !candidateInfo.email?.trim()) {
                        setError('Enter your full name and email address to continue.');
                      } else if (!candidateInfo.fullName?.trim()) {
                        setError('Enter your full name to continue.');
                      } else {
                        setError('Enter your email address to continue.');
                      }
                      return;
                    }

                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(candidateInfo.email.trim())) {
                      setError('Enter a valid email address (e.g. name@example.com).');
                      return;
                    }

                    try {
                      const response = await fetch(`${API_URL}/questions?assessment=${encodeURIComponent(currentAssessment.slug)}`);
                      if (response.status === 404) {
                        setView('unavailable');
                        return;
                      }
                      if (!response.ok) throw new Error('Failed to load questions');
                      const allQuestions = await response.json();
                      const enabled = allQuestions.filter(q => q.enabled);
                      setEnabledQuestions(enabled);
                      // An untouched ordering question is answered as shown; matching starts unanswered
                      setAnswers(Object.fromEntries(enabled
                        .filter(q => q.type === 'ordering' || q.type === 'matching')
                        .map(q => [q.id.toString(), q.type === 'ordering' ? [...q.options] : q.options.map(() => '')])));
                      setView('assessment');
                      setError(null);
                    } catch (err) {
                      console.error('Error loading questions:', err);
                      setError('Unable to load the assessment. Check your connection and try again.');
                    }
                  }}
                  className="w-full bg-invizio text-white py-4 min-h-[52px] text-base font-semibold rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 transition-colors"
                >
                  Start Assessment
                </button>
                {error && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
                    {error}
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mt-1.5">One attempt. Progress is saved if you leave and return to the assessment later. Your score and results will be shown at completion.</p>
                  
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="w-full bg-gray-800 text-white py-3 mt-auto">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-sm">
              &copy; {new Date().getFullYear()} Invizio. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  const totalQuestions = enabledQuestions.length || 0;
  const progress = totalQuestions > 0 ? (currentQuestion / totalQuestions) * 100 : 0;

  // Get current question
  const currentQuestionData = enabledQuestions.length > 0 && currentQuestion > 0 
    ? enabledQuestions[currentQuestion - 1] 
    : null;

  const renderQuestion = () => {
    if (!currentQuestionData) {
      return (
        <div className="py-12 text-center" aria-live="polite">
          <p className="text-base text-gray-600">Loading questions...</p>
        </div>
      );
    }

    const question = currentQuestionData;
    const questionId = question.id.toString();

    // Render based on question type — question first (prompt-like), then input/label, helpers last
    const pointsLabel = question.points === 1 ? '1 point' : `${question.points} points`;
    if (question.type === 'short-answer') {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-medium text-invizio-ink leading-snug whitespace-pre-line">
              {question.text ?? ''}
            </h3>
            <p className="text-xs text-gray-400 mt-1.5">
              {pointsLabel}
            </p>
          </div>
          <div>
            <label htmlFor={`answer-${questionId}`} className="block text-sm font-medium text-gray-700 mb-2">
              Your answer
            </label>
            <textarea
              id={`answer-${questionId}`}
              className="w-full px-4 py-3 text-base border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:ring-offset-0 focus:border-invizio/50 min-h-[120px]"
              rows={6}
              placeholder="Type your answer here..."
              value={answers[questionId] || ''}
              onChange={(e) => setAnswers({ ...answers, [questionId]: e.target.value })}
            />
          </div>
        </div>
      );
    } else if (question.type === 'fill-in') {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-medium text-invizio-ink leading-snug whitespace-pre-line">
              {question.text ?? ''}
            </h3>
            <p className="text-xs text-gray-400 mt-1.5">
              Short exact answer · {pointsLabel}
            </p>
          </div>
          <div>
            <label htmlFor={`answer-${questionId}`} className="block text-sm font-medium text-gray-700 mb-2">
              Your answer
            </label>
            <input
              id={`answer-${questionId}`}
              type="text"
              autoComplete="off"
              className="w-full max-w-md px-4 py-3 min-h-[48px] text-base border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:ring-offset-0 focus:border-invizio/50"
              value={answers[questionId] || ''}
              onChange={(e) => setAnswers({ ...answers, [questionId]: e.target.value })}
            />
          </div>
        </div>
      );
    } else if (question.type === 'ordering') {
      const order = answers[questionId] || question.options || [];
      const move = (idx, delta) => {
        const next = [...order];
        [next[idx], next[idx + delta]] = [next[idx + delta], next[idx]];
        setAnswers({ ...answers, [questionId]: next });
      };
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-medium text-invizio-ink leading-snug whitespace-pre-line">
              {question.text ?? ''}
            </h3>
            <p className="text-xs text-gray-400 mt-1.5">
              Use the arrows to put the items in order, first at the top · {pointsLabel}
            </p>
          </div>
          <ol className="space-y-3">
            {order.map((item, idx) => (
              <li key={item} className="flex items-center gap-3 p-3 min-h-[48px] border border-gray-200 rounded-lg bg-white">
                <span className="w-6 text-sm font-semibold text-gray-500 shrink-0 text-center">{idx + 1}</span>
                <span className="flex-1 text-base text-gray-700">{item}</span>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    aria-label={`Move "${item}" up`}
                    className="w-10 h-10 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-invizio"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === order.length - 1}
                    aria-label={`Move "${item}" down`}
                    className="w-10 h-10 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-invizio"
                  >
                    ↓
                  </button>
                </div>
              </li>
            ))}
          </ol>
        </div>
      );
    } else if (question.type === 'matching') {
      const selections = answers[questionId] || (question.options || []).map(() => '');
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-medium text-invizio-ink leading-snug whitespace-pre-line">
              {question.text ?? ''}
            </h3>
            <p className="text-xs text-gray-400 mt-1.5">
              Choose a match for each item · {pointsLabel}
            </p>
          </div>
          <div className="space-y-3">
            {(question.options || []).map((item, idx) => (
              <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 border border-gray-200 rounded-lg bg-white">
                <label htmlFor={`answer-${questionId}-${idx}`} className="flex-1 text-base text-gray-700">
                  {item}
                </label>
                <select
                  id={`answer-${questionId}-${idx}`}
                  className="sm:w-64 px-3 py-2.5 min-h-[44px] text-base border border-gray-200 rounded-lg bg-white focus:ring-1 focus:ring-invizio focus:border-invizio/50"
                  value={selections[idx] || ''}
                  onChange={(e) => setAnswers({ ...answers, [questionId]: selections.map((sel, i) => i === idx ? e.target.value : sel) })}
                >
                  <option value="">Choose…</option>
                  {(question.choices || []).map(choice => (
                    <option key={choice} value={choice}>{choice}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      );
    } else if (question.type === 'multiple-select') {
      return (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-medium text-invizio-ink leading-snug whitespace-pre-line">
              {question.text ?? ''}
            </h3>
            <p className="text-xs text-gray-400 mt-1.5">
              Select all that apply · {pointsLabel}
            </p>
          </div>
          <div className="space-y-3">
            {question.options && question.options.map((option, idx) => {
              const isSelected = (answers[questionId] || []).includes(option);
              return (
                <label key={idx} className={`flex items-center p-4 min-h-[48px] border rounded-lg cursor-pointer text-left transition-colors focus-within:ring-1 focus-within:ring-invizio focus-within:ring-offset-0 focus-within:border-invizio/50 ${isSelected ? 'ring-1 ring-invizio/40 border-invizio/50 bg-invizio-tint' : 'border-gray-200 hover:bg-gray-50/80'}`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleMultiSelect(questionId, option)}
                    className="mr-4 w-5 h-5 shrink-0 focus:ring-0 focus:outline-none"
                  />
                  <span className="text-base text-gray-700">{option ?? ''}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    } else {
      // multiple-choice
      return (
        <div className="space-y-4">
          <div>
            <h3 className="text-xl font-medium text-invizio-ink leading-snug whitespace-pre-line">
              {question.text ?? ''}
            </h3>
            <p className="text-xs text-gray-400 mt-1.5">
              Select one answer · {pointsLabel}
            </p>
          </div>
          <div className="space-y-3">
            {question.options && question.options.map((option, idx) => {
              const isSelected = answers[questionId] === option;
              return (
                <label key={idx} className={`flex items-center p-4 min-h-[48px] border rounded-lg cursor-pointer text-left transition-colors focus-within:ring-1 focus-within:ring-invizio focus-within:ring-offset-0 focus-within:border-invizio/50 ${isSelected ? 'ring-1 ring-invizio/40 border-invizio/50 bg-invizio-tint' : 'border-gray-200 hover:bg-gray-50/80'}`}>
                  <input
                    type="radio"
                    name={questionId}
                    value={option}
                    checked={isSelected}
                    onChange={(e) => setAnswers({ ...answers, [questionId]: e.target.value })}
                    className="mr-4 w-5 h-5 shrink-0 focus:ring-0 focus:outline-none"
                  />
                  <span className="text-base text-gray-700">{option ?? ''}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    }
  };

  if (isGrading) {
    return (
      <div className="min-h-screen bg-page-gradient flex items-center justify-center p-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 max-w-md text-center">
          <Clock className="w-16 h-16 text-invizio mx-auto mb-6 animate-spin" aria-hidden="true" />
          <h2 className="text-xl font-bold text-invizio-ink mb-3">Grading your assessment</h2>
          <p className="text-base text-gray-600 leading-relaxed">
            Please wait while we evaluate your answers. This may take a moment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page-gradient flex flex-col">
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {/* Progress: helper first, clear and visible */}
            <div className="mb-10">
              <p className="text-sm font-medium text-gray-600 mb-2" aria-live="polite">
                Question {currentQuestion} of {totalQuestions}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2.5" aria-hidden="true">
                <div className="bg-invizio h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {/* Question first (main content), one per screen */}
            <div className="mb-10">
              {renderQuestion()}
            </div>

            {/* Actions: secondary left, primary right — consistent across steps */}
            <div className="flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center gap-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to exit? Your progress will not be saved.')) {
                      setView('candidate');
                      setCurrentQuestion(1);
                      setAnswers({});
                      setError(null);
                    }
                  }}
                  className="px-5 py-3 min-h-[48px] text-base bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 font-medium"
                >
                  Exit Assessment
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentQuestion(Math.max(1, currentQuestion - 1))}
                  disabled={currentQuestion === 1}
                  className="px-5 py-3 min-h-[48px] text-base bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-200"
                >
                  Previous
                </button>
              </div>
              {currentQuestion < totalQuestions ? (
                <button
                  type="button"
                  onClick={() => setCurrentQuestion(currentQuestion + 1)}
                  className="w-full sm:w-auto px-8 py-3 min-h-[48px] text-base font-semibold bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 transition-colors"
                >
                  Next
                </button>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isGrading}
                    className="w-full sm:w-auto px-8 py-3 min-h-[48px] text-base font-semibold bg-invizio text-white rounded-lg hover:bg-invizio-dark focus:outline-none focus:ring-2 focus:ring-invizio focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-invizio transition-colors"
                  >
                    Submit Assessment
                  </button>
                  {error && (
                    <span className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
                      {error}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full bg-gray-800 text-white py-3 mt-auto">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm">
            &copy; {new Date().getFullYear()} Invizio. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TechnicalAssessment;
            
